import { useEffect, useState } from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material'
import { formatDate } from '../../utils/formatting'

// must match MAX_OBSTRUCTION_REPORT_EXTENSION_DAYS in the API
export const MAX_EXPIRY_DAYS = 365

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Mirrors the API's extend: counts from the current expiry, or from now if that has already passed.
 */
export const getExtendedExpiry = (currentExpiry: string, days: number, now = Date.now()): Date =>
  new Date(Math.max(new Date(currentExpiry).getTime(), now) + days * DAY_MS)

/**
 * Mirrors the API's reactivate: always counts from now.
 */
export const getReactivatedExpiry = (days: number, now = Date.now()): Date => new Date(now + days * DAY_MS)

interface ExpiryDaysDialogProps {
  open: boolean
  title: string
  description: string
  confirmLabel: string
  getNewExpiry: (days: number) => Date
  onClose: () => void
  onConfirm: (days: number) => void
}

/**
 * Asks for a whole number of days and previews the resulting expiry. Used to extend an active
 * obstruction report and to reactivate an archived one.
 */
const ExpiryDaysDialog = ({
  open,
  title,
  description,
  confirmLabel,
  getNewExpiry,
  onClose,
  onConfirm,
}: ExpiryDaysDialogProps) => {
  const [daysInput, setDaysInput] = useState('7')

  // start from the default each time the dialog opens
  useEffect(() => {
    if (open) setDaysInput('7')
  }, [open])

  const days = Number(daysInput)
  const isValid = daysInput.trim() !== '' && Number.isInteger(days) && days >= 1 && days <= MAX_EXPIRY_DAYS

  return (
    <Dialog fullWidth maxWidth="xs" open={open} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (isValid) onConfirm(days)
        }}
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2">{description}</Typography>
            <TextField
              autoFocus
              label="Days"
              type="number"
              value={daysInput}
              onChange={(e) => setDaysInput(e.target.value)}
              inputProps={{ min: 1, max: MAX_EXPIRY_DAYS, step: 1 }}
              error={!isValid}
              helperText={
                isValid
                  ? `New expiry: ${formatDate(getNewExpiry(days))}`
                  : `Enter a whole number from 1 to ${MAX_EXPIRY_DAYS}`
              }
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={!isValid}>
            {confirmLabel}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default ExpiryDaysDialog
